import * as z from "zod";
import { userFacingError } from "../authorization";
import { supportsGitAuth } from "../connectors/gitSyncCapabilities";
import { matchesUrlPattern } from "../connectors/matchesUrlPattern";
import {
  dropConnectorUsage,
  recordConnectorUsage,
  usageRef,
} from "../connectorUsages";
import { zql } from "../generatedDrizzleSchema";
import {
  canonicalRepoUrl,
  GIT_SYNC_ROOT,
  gitSyncDirectoriesOverlap,
  normalizeGitSyncDirectory,
} from "../gitSyncRemote";
import { assertSupplierAvailable } from "../rbac/crossResourceAvailability";
import { mutator } from "./mutator";

export const configureGitSyncSchema = z.object({
  spaceId: z.string(),
  remoteUrl: z.string().url().startsWith("https://"),
  connectedAppId: z.string(),
  directory: z.string().optional(),
});

export const configureGitSync = mutator({
  schema: configureGitSyncSchema,
  resolve: {
    space: { kind: "space", from: "spaceId" },
    app: { kind: "connectedApp", from: "connectedAppId" },
  },
  permissions: [
    {
      permission: "space:manage",
      scopeId: ({ args }) => args.space.id,
    },
    {
      permission: "connected_app:use",
      scopeId: ({ args }) => args.app.id,
    },
  ],
  fn: async ({ tx, ctx, args: { space, app, remoteUrl, directory } }) => {
    if (!supportsGitAuth(app.identifier)) {
      throw userFacingError("Connector does not support git sync");
    }
    const normalizedDirectory = normalizeGitSyncDirectory(directory);
    if (normalizedDirectory === undefined) {
      throw userFacingError("Repository directory is not a valid path");
    }

    await assertSupplierAvailable({
      tx,
      tenantId: ctx.tenantId,
      ruleId: "space_uses_connected_apps",
      dependentScopeId: space.id,
      supplierScopeId: app.id,
    });

    const parsedRemote = new URL(remoteUrl);
    if (parsedRemote.username || parsedRemote.password) {
      throw userFacingError(
        "Repository URLs must not contain embedded credentials"
      );
    }
    if (!matchesUrlPattern(app.urlPattern, remoteUrl)) {
      throw userFacingError(
        "Repository URL must match the connector’s URL pattern"
      );
    }

    // Spaces may share a repository, but each must own a disjoint subtree —
    // nested or equal directories would have two spaces rewriting the same
    // paths against each other on every sync.
    const remotes = await tx.run(
      zql.spaceGitRemotes.where("tenantId", ctx.tenantId)
    );
    const canonicalUrl = canonicalRepoUrl(remoteUrl);
    for (const remote of remotes) {
      if (remote.spaceId === space.id) continue;
      if (canonicalRepoUrl(remote.remoteUrl) !== canonicalUrl) continue;
      if (gitSyncDirectoriesOverlap(remote.directory, normalizedDirectory)) {
        throw userFacingError(
          "Another space already syncs with this repository’s " +
            (remote.directory === GIT_SYNC_ROOT
              ? "root"
              : `“${remote.directory}” directory`) +
            "; choose a directory outside it"
        );
      }
    }

    const ref = usageRef.spaceGitRemote(space.id);
    const [existingUsage] = await tx.run(
      zql.connectedAppUsages.where("type", ref.type).where("refId", ref.refId)
    );
    if (existingUsage && existingUsage.connectedAppId !== app.id) {
      await dropConnectorUsage(tx, existingUsage.connectedAppId, ref);
    }
    await tx.mutate.spaceGitRemotes.upsert({
      spaceId: space.id,
      remoteUrl,
      directory: normalizedDirectory,
      lastError: null,
      lastSyncedAt: null,
      syncStartedAt: null,
      webhookReceivedAt: null,
      webhookConfiguredAt: null,
      webhookError: null,
      tenantId: ctx.tenantId,
    });
    if (!existingUsage || existingUsage.connectedAppId !== app.id) {
      await recordConnectorUsage(tx, app.id, ref, ctx.tenantId);
    }
  },
});
